import {
  useCheckoutAppUrl,
  useCheckoutFrontendHost,
  useCheckoutShopsApiUrl,
  useShopId,
} from '@aboutyou/config';
import {DataFetcher, useRequest} from '@aboutyou/data-fetcher';
import {Head} from '@aboutyou/head-elements';
import {useCOTracking} from '@aboutyou/tracking';
import React, {useEffect} from 'react';
import {CHECKOUT_WC_JS_PATH} from './checkout-constants';
import {getCheckoutPreloadData} from './getCheckoutPreloadData';
import type {CheckoutPreloadData} from './types';

export const CheckoutPreloadView = ({
  version,
  hash,
  content,
}: CheckoutPreloadData) => {
  const checkoutShopsUrl = useCheckoutShopsApiUrl();
  const checkoutAppUrl = useCheckoutAppUrl();
  const shopId = useShopId();
  const checkoutFrontendHost = useCheckoutFrontendHost();

  const coLoadScript = `${checkoutFrontendHost}/${CHECKOUT_WC_JS_PATH}?appId=${shopId}`;

  return (
    <Head>
      <link rel="preload" as="script" href={`${coLoadScript}`} />
      <script type="text/javascript" src={`${coLoadScript}`} defer></script>

      <script type="text/javascript" id="checkout-load-script">
        {content}
      </script>

      <link
        rel="preconnect"
        href="https://checkout-v3.aboutyou.de"
        data-testid="checkoutPreload"
      />
      <link
        rel="preconnect"
        href="https://checkout-cdn.aboutyou.cloud"
        data-testid="checkoutPreload"
      />
      <link
        rel="preconnect"
        data-testid="checkoutPreload"
        href="https://cot.aboutyou.com"
      />
      <link
        rel="preload"
        href={`${checkoutShopsUrl}/ay/config.json`}
        as="fetch"
        data-testid="checkoutPreload"
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={`${checkoutAppUrl}/${version}/${hash}/ay-checkout-wc.min.js`}
        as="script"
        data-testid="checkoutPreload"
        crossOrigin="anonymous"
      />
      <link
        rel="preload"
        href={`${checkoutAppUrl}/${version}/${hash}/ay-checkout-base.min.css`}
        as="style"
        data-testid="checkoutPreload"
        fetchPriority="low"
      />
      <link
        rel="preload"
        href={`${checkoutShopsUrl}/ay/overwrites.css`}
        as="style"
        data-testid="checkoutPreload"
        fetchPriority="low"
      />
    </Head>
  );
};

const ErrorView = React.memo(function ErrorView({error}: {error: unknown}) {
  const errorLogged = React.useRef(false);
  const {track} = useCOTracking();
  const checkoutShopsUrl = useCheckoutShopsApiUrl();

  const errorMessage = (error as Error)?.message ?? 'unknown error';

  useEffect(() => {
    if (errorLogged.current) {
      return;
    }
    errorLogged.current = true;
    track({
      type: 'ComponentPreloadError',
      message: `Checkout Preload Failed: ${errorMessage}`,
    });
  }, []);

  return (
    <Head>
      <link
        rel="preconnect"
        href="https://checkout-v3.aboutyou.de"
        data-testid="checkoutPreload"
      />
      <link
        rel="preconnect"
        href="https://checkout-cdn.aboutyou.cloud"
        data-testid="checkoutPreload"
      />
      <link
        rel="preconnect"
        href="https://cot.aboutyou.com"
        data-testid="checkoutPreload"
      />
      <link
        rel="preload"
        href={`${checkoutShopsUrl}/ay/overwrites.css`}
        as="style"
        data-testid="checkoutPreload"
      />
      <link
        rel="preload"
        href={`${checkoutShopsUrl}/ay/config.json`}
        as="fetch"
        data-testid="checkoutPreload"
        crossOrigin="anonymous"
      />
    </Head>
  );
});

const preloadFetch = (shopId: number, checkoutFrontendHost: string) => {
  return async () => {
    if (process.browser) {
      return fetch(`/__/checkout_preload?shopId=${shopId}`).then(res =>
        res.json(),
      );
    } else {
      return getCheckoutPreloadData(shopId, checkoutFrontendHost);
    }
  };
};

export const CheckoutPreload: React.FC = () => {
  const shopId = useShopId();
  const checkoutFrontendHost = useCheckoutFrontendHost();

  const preloadRequest = useRequest(
    'checkoutPreload',
    preloadFetch(shopId, checkoutFrontendHost),
    {retry: false},
  );

  return (
    <DataFetcher
      request={preloadRequest}
      View={CheckoutPreloadView}
      viewProps={{shopId}}
      Error={(errorProps: {error: unknown}) => (
        <ErrorView error={errorProps.error} />
      )}
    />
  );
};
